import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFornecedoresCombo, type ProdutoFornecedorRow, type SalvarFornecedorRequest } from "@/features/produtos/hooks/useProdutoFornecedores";

const EMPTY: SalvarFornecedorRequest = {
  fornecedorId: 0, codigoProdutoFornecedor: null, codigoBarrasFornecedor: null,
  precoCompra: null, lucroEsperado: null, custos: null, multiplicaDivide: false,
};

interface Props {
  open:    boolean;
  row:     ProdutoFornecedorRow | null;
  onClose: () => void;
  onSave:  (data: SalvarFornecedorRequest, id?: number) => void;
}

export function FornecedorDialog({ open, row, onClose, onSave }: Props) {
  const fornecedores = useFornecedoresCombo();
  const [f, setF] = useState<SalvarFornecedorRequest>(EMPTY);

  useEffect(() => {
    if (open) setF(row ? {
      fornecedorId: row.fornecedorId, codigoProdutoFornecedor: row.codigoProdutoFornecedor ?? null,
      codigoBarrasFornecedor: row.codigoBarrasFornecedor ?? null, precoCompra: row.precoCompra ?? null,
      lucroEsperado: row.lucroEsperado ?? null, custos: row.custos ?? null, multiplicaDivide: row.multiplicaDivide,
    } : EMPTY);
  }, [open, row]);

  const set = <K extends keyof SalvarFornecedorRequest>(k: K, v: SalvarFornecedorRequest[K]) => setF(p => ({ ...p, [k]: v }));
  const numField = (label: string, key: "precoCompra" | "lucroEsperado" | "custos") => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type="number" step="0.01" min="0" className="h-8 text-sm"
        value={f[key] ?? ""} onChange={(e) => set(key, e.target.value === "" ? null : Number(e.target.value))} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{row ? "Editar" : "Vincular"} fornecedor</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Fornecedor *</Label>
            <Select value={f.fornecedorId ? String(f.fornecedorId) : "__none__"} onValueChange={(v) => set("fornecedorId", v === "__none__" ? 0 : Number(v))}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Selecione o fornecedor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {fornecedores.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.nomeRazao}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Código do produto no fornecedor</Label>
              <Input className="h-8 text-sm" value={f.codigoProdutoFornecedor ?? ""} onChange={(e) => set("codigoProdutoFornecedor", e.target.value || null)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Código de barras do fornecedor</Label>
              <Input className="h-8 text-sm" value={f.codigoBarrasFornecedor ?? ""} onChange={(e) => set("codigoBarrasFornecedor", e.target.value || null)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {numField("Preço de Compra (R$)", "precoCompra")}
            {numField("Lucro Esperado (%)", "lucroEsperado")}
            {numField("Custos Extras (R$)", "custos")}
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={f.multiplicaDivide} onCheckedChange={(v) => set("multiplicaDivide", v)} />
            <Label className="text-sm">Multiplica (ligado) / Divide (desligado) fator de conversão</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onSave(f, row?.id)} disabled={!f.fornecedorId}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
